import { Head, Link, router } from "@inertiajs/react"
import { SquarePen } from "lucide-react"
import { useState } from "react"

import { HpiDataTable, HpiFilterSelect, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  customer_id: number | null
  customer_name: string | null
  investigation_type: string | null
  objectives: string | null
  manager: string | null
  intake_name: string | null
  rep_initials: string | null
  location: string | null
  open_date: string | null
  closed_date: string | null
  status: number
  display_status: string
  hpi_company: number | null
  open: boolean
}

interface Permissions {
  can_search: boolean
  can_view_customers: boolean
  can_add_case: boolean
  can_view_closed: boolean
  can_view_case_payment: boolean
}

interface FilterOptions {
  companies: { id: number; name: string }[]
  managers: { id: number; name: string }[]
  investigation_types: { id: number; name: string }[]
  statuses: { value: number; label: string }[]
}

interface Filters {
  search_name: string
  manager_id: string
  type_id: string
  status: string
  company: string
}

interface CustomerContext {
  id: number
  name: string
}

interface Props {
  active_cases?: CaseRecord[]
  pending_report_cases?: CaseRecord[]
  pending_payment_cases?: CaseRecord[]
  potential_cases?: CaseRecord[]
  search_results?: CaseRecord[]
  customer_context?: CustomerContext
  permissions: Permissions
  filter_options: FilterOptions
  filters: Filters
}

/* ── Helpers ────────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [{ title: "Cases", href: "/cases" }]
const PER_PAGE = 50

function buildSearchDescription(
  filters: Filters,
  filterOptions: FilterOptions,
): string {
  const parts: string[] = []
  if (filters.search_name) parts.push(`Name: "${filters.search_name}"`)
  if (filters.company) {
    const co = filterOptions.companies.find(
      (c) => String(c.id) === filters.company,
    )
    if (co) parts.push(`Company: ${co.name}`)
  }
  if (filters.manager_id) {
    const mgr = filterOptions.managers.find(
      (m) => String(m.id) === filters.manager_id,
    )
    if (mgr) parts.push(`Manager: ${mgr.name}`)
  }
  if (filters.type_id) {
    const t = filterOptions.investigation_types.find(
      (t) => String(t.id) === filters.type_id,
    )
    if (t) parts.push(`Type: ${t.name}`)
  }
  if (filters.status) {
    const s = filterOptions.statuses.find(
      (s) => String(s.value) === filters.status,
    )
    if (s) parts.push(`Status: ${s.label}`)
  }
  return parts.join(", ")
}

/* ── Column Builder ─────────────────────────────────────── */

function buildColumns(
  permissions: Permissions,
  showStatus: boolean,
): HpiColumn<CaseRecord>[] {
  const cols: HpiColumn<CaseRecord>[] = []

  // Col: Date Opened / Intake (only when can_add_case — matches legacy perm_addCase gating)
  if (permissions.can_add_case) {
    cols.push({
      header: "Date Opened",
      subHeader: permissions.can_view_customers ? "Intake" : undefined,
      width: "max-w-[140px]",
      hideOnMobile: true,
      render: (row) => (
        <div>
          <div className="font-medium">{formatDate(row.open_date)}</div>
          {permissions.can_view_customers && row.intake_name && (
            <div className="text-hpi-dark-gray text-sm">{row.intake_name}</div>
          )}
        </div>
      ),
    })
  }

  // Col: Case Name / Customer (+ Intake sub when Date Opened column is hidden)
  cols.push({
    header: "Case Name",
    subHeader: permissions.can_view_customers ? "Customer" : undefined,
    render: (row) => (
      <div>
        <Link
          href={`/cases/${row.id}`}
          className="text-hpi-dark-gray hover:text-hpi-active-blue font-semibold"
        >
          {row.display_name}
        </Link>
        {permissions.can_view_customers && row.customer_name && (
          <div className="text-hpi-med-blue text-sm">
            {row.customer_id ? (
              <Link
                href={`/customers/${row.customer_id}`}
                className="hover:text-hpi-active-blue"
              >
                {row.customer_name}
              </Link>
            ) : (
              row.customer_name
            )}
          </div>
        )}
        {!permissions.can_add_case &&
          permissions.can_view_customers &&
          row.intake_name && (
            <div className="text-hpi-dark-gray text-xs">{row.intake_name}</div>
          )}
      </div>
    ),
  })

  // Col: Type / Objectives
  cols.push({
    header: "Type",
    subHeader: "Objectives",
    hideOnMobile: true,
    render: (row) => (
      <div>
        <div>{row.investigation_type ?? ""}</div>
        {row.objectives && (
          <div className="text-hpi-dark-gray text-sm">{row.objectives}</div>
        )}
      </div>
    ),
  })

  // Col: Manager / Rep
  cols.push({
    header: "Manager",
    subHeader: permissions.can_add_case ? "Rep" : undefined,
    width: "max-w-[140px]",
    render: (row) => (
      <div>
        <div>{row.manager ?? ""}</div>
        {permissions.can_add_case && row.rep_initials && (
          <div className="text-hpi-dark-gray text-sm">{row.rep_initials}</div>
        )}
      </div>
    ),
  })

  if (showStatus) {
    cols.push({
      header: "Status",
      width: "max-w-[140px]",
      render: (row) => <div className="text-sm">{row.display_status}</div>,
    })
  }

  return cols
}

/* ── Paginated Table ────────────────────────────────────── */

function PaginatedCaseTable({
  title,
  cases,
  columns,
  actions,
}: {
  title: string
  cases: CaseRecord[]
  columns: HpiColumn<CaseRecord>[]
  actions?: { label: string; icon: typeof SquarePen; onClick?: () => void }[]
}) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(cases.length / PER_PAGE)
  const paginated = cases.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  return (
    <HpiDataTable<CaseRecord>
      title={title}
      actions={actions}
      columns={columns}
      data={paginated}
      keyFn={(row) => row.id}
      rowHref={(row) => `/cases/${row.id}`}
      className="mb-6"
      pagination={
        cases.length > PER_PAGE ? (
          <HpiPagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={cases.length}
            perPage={PER_PAGE}
            onPageChange={setPage}
          />
        ) : undefined
      }
    />
  )
}

/* ── Search Bar ─────────────────────────────────────────── */

function SearchBar({
  filters,
  filterOptions,
}: {
  filters: Filters
  filterOptions: FilterOptions
}) {
  const [searchName, setSearchName] = useState(filters.search_name)
  const [company, setCompany] = useState(filters.company)
  const [managerId, setManagerId] = useState(filters.manager_id)
  const [typeId, setTypeId] = useState(filters.type_id)
  const [status, setStatus] = useState(filters.status)

  const showCompanyFilter = filterOptions.companies.length > 1

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (searchName) params.search_name = searchName
    if (company) params.company = company
    if (managerId) params.manager_id = managerId
    if (typeId) params.type_id = typeId
    if (status) params.status = status
    router.get("/cases", params, { preserveState: true })
  }

  const handleClear = () => {
    setSearchName("")
    setCompany("")
    setManagerId("")
    setTypeId("")
    setStatus("")
    router.get("/cases", {}, { preserveState: true })
  }

  const hasFilters = searchName || company || managerId || typeId || status

  return (
    <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
      <div>
        <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
          Search
        </label>
        <input
          type="text"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
          placeholder="Name, case #, report..."
          className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 w-48 rounded border bg-white px-2 text-sm focus:ring-1"
        />
      </div>
      {showCompanyFilter && (
        <HpiFilterSelect
          label="Company"
          value={company}
          onChange={setCompany}
          options={filterOptions.companies.map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          placeholder="All Companies"
        />
      )}
      <HpiFilterSelect
        label="Manager"
        value={managerId}
        onChange={setManagerId}
        options={filterOptions.managers.map((m) => ({
          value: m.id,
          label: m.name,
        }))}
        placeholder="All Managers"
      />
      <HpiFilterSelect
        label="Type"
        value={typeId}
        onChange={setTypeId}
        options={filterOptions.investigation_types.map((t) => ({
          value: t.id,
          label: t.name,
        }))}
        placeholder="All Types"
      />
      <HpiFilterSelect
        label="Status"
        value={status}
        onChange={setStatus}
        options={filterOptions.statuses.map((s) => ({
          value: s.value,
          label: s.label,
        }))}
        placeholder="All Statuses"
      />
      <button
        onClick={handleUpdate}
        className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
      >
        Search
      </button>
      {hasFilters && (
        <button
          onClick={handleClear}
          className="text-hpi-med-gray hover:text-hpi-dark-gray h-8 px-3 text-sm transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  )
}

/* ── Main Component ─────────────────────────────────────── */

export default function Index({
  active_cases,
  pending_report_cases,
  pending_payment_cases,
  potential_cases,
  search_results,
  customer_context,
  permissions,
  filter_options,
  filters,
}: Props) {
  const isSearchView = !!search_results
  const columns = buildColumns(permissions, isSearchView)
  const groupColumns = buildColumns(permissions, false)

  const addAction = permissions.can_add_case
    ? [
        {
          label: "New Case",
          icon: SquarePen,
          onClick: () => {
            window.location.href = "/cases/new"
          },
        },
      ]
    : undefined

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Cases" />

      <div className="p-6">
        {customer_context && (
          <div className="text-hpi-dark-gray mb-4 flex items-center gap-2 text-sm">
            <Link
              href={`/customers/${customer_context.id}`}
              className="text-hpi-active-blue font-medium hover:underline"
            >
              {customer_context.name}
            </Link>
            <span className="text-hpi-med-gray">/</span>
            <span>Cases</span>
          </div>
        )}

        {permissions.can_search && !customer_context && (
          <SearchBar filters={filters} filterOptions={filter_options} />
        )}

        {isSearchView ? (
          <>
            {!customer_context &&
              (() => {
                const desc = buildSearchDescription(filters, filter_options)
                return desc ? (
                  <p className="text-hpi-dark-gray -mt-4 mb-4 text-xs">
                    Filtered by {desc}
                  </p>
                ) : null
              })()}
            {search_results.length > 0 ? (
              <PaginatedCaseTable
                title={
                  customer_context
                    ? `Cases for ${customer_context.name} (${search_results.length})`
                    : `Search Results (${search_results.length})`
                }
                cases={search_results}
                columns={columns}
              />
            ) : (
              <>
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  No cases found matching your search criteria.
                </div>
                {(active_cases?.length ?? 0) > 0 && (
                  <PaginatedCaseTable
                    title={`Active Cases (${active_cases!.length})`}
                    cases={active_cases!}
                    columns={groupColumns}
                    actions={addAction}
                  />
                )}
                {(potential_cases?.length ?? 0) > 0 && (
                  <HpiDataTable<CaseRecord>
                    title={`Newest Potential Cases (${potential_cases!.length})`}
                    columns={groupColumns}
                    data={potential_cases!}
                    keyFn={(row) => row.id}
                    rowHref={(row) => `/cases/${row.id}`}
                    className="mb-6"
                  />
                )}
              </>
            )}
          </>
        ) : (
          <>
            {(active_cases?.length ?? 0) > 0 && (
              <HpiDataTable<CaseRecord>
                title={`Active Cases (${active_cases!.length})`}
                actions={addAction}
                columns={groupColumns}
                data={active_cases!}
                keyFn={(row) => row.id}
                rowHref={(row) => `/cases/${row.id}`}
                className="mb-6"
              />
            )}

            {(pending_report_cases?.length ?? 0) > 0 && (
              <HpiDataTable<CaseRecord>
                title={`Pending Report (${pending_report_cases!.length})`}
                columns={groupColumns}
                data={pending_report_cases!}
                keyFn={(row) => row.id}
                rowHref={(row) => `/cases/${row.id}`}
                className="mb-6"
              />
            )}

            {(pending_payment_cases?.length ?? 0) > 0 && (
              <HpiDataTable<CaseRecord>
                title={`Pending Final Payment (${pending_payment_cases!.length})`}
                columns={groupColumns}
                data={pending_payment_cases!}
                keyFn={(row) => row.id}
                rowHref={(row) => `/cases/${row.id}`}
                className="mb-6"
              />
            )}

            {(potential_cases?.length ?? 0) > 0 && (
              <HpiDataTable<CaseRecord>
                title={`Newest Potential Cases (${potential_cases!.length})`}
                columns={groupColumns}
                data={potential_cases!}
                keyFn={(row) => row.id}
                rowHref={(row) => `/cases/${row.id}`}
                className="mb-6"
              />
            )}

            {!active_cases?.length &&
              !pending_report_cases?.length &&
              !pending_payment_cases?.length &&
              !potential_cases?.length && (
                <div className="text-hpi-med-gray rounded-lg bg-white p-8 text-center text-sm">
                  No active cases found.
                </div>
              )}
          </>
        )}
      </div>
    </AppLayout>
  )
}
